import { MVP_CONFIG } from '../config/mvpConfig';
import {
  LAUNCH_INVENTORY,
  LESSON_CONTENT_PATCHES,
  LOCAL_SHIPPABLE_JOURNEYS,
  OFFLINE_INCOMPLETE_JOURNEYS,
  PLACEHOLDER_LESSON_SLUGS,
} from '../data/launchLearningCatalog';
import type { JourneyPath, Lesson, Module } from '../types/journey';

const SHIPPABLE = new Set(MVP_CONFIG.courses.shippableSlugs);

function asBlocks(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function lessonHasBody(lesson: Lesson | null | undefined): boolean {
  if (!lesson) return false;
  if (typeof lesson.bodyMy === 'string' && lesson.bodyMy.trim()) return true;
  if (typeof lesson.bodyEn === 'string' && lesson.bodyEn.trim()) return true;
  if (typeof lesson.content === 'string' && lesson.content.trim()) return true;
  return asBlocks(lesson.content).length > 0;
}

function fillMissing(existing: Lesson, patch: Partial<Lesson> | undefined): Lesson {
  if (!patch) return existing;
  const next: Lesson = { ...existing };

  if (patch.title && (existing.title || '').trim().toLowerCase() === 'intenet') {
    next.title = patch.title;
  }
  if (!existing.yourKeySkill && patch.yourKeySkill) next.yourKeySkill = patch.yourKeySkill;
  if (asBlocks(existing.introduction).length === 0 && patch.introduction) {
    next.introduction = patch.introduction;
  }
  if (asBlocks(existing.content).length === 0 && patch.content) {
    next.content = patch.content;
  }
  if (asBlocks(existing.keyTakeaways).length === 0 && patch.keyTakeaways) {
    next.keyTakeaways = patch.keyTakeaways;
  }
  if ((!existing.measurableDeliverables || existing.measurableDeliverables.length === 0) && patch.measurableDeliverables) {
    next.measurableDeliverables = patch.measurableDeliverables;
  }
  if ((!existing.actionableTasks || existing.actionableTasks.length === 0) && patch.actionableTasks) {
    next.actionableTasks = patch.actionableTasks;
  }
  if (!existing.associatedQuiz && patch.associatedQuiz) {
    next.associatedQuiz = patch.associatedQuiz;
  }
  if (!existing.type && patch.type) next.type = patch.type;
  if (!existing.duration && patch.duration) next.duration = patch.duration;

  return next;
}

function prepareModule(module: Module, shippable: boolean): Module | null {
  const lessons = (module.lessons || [])
    .filter((lesson) => (shippable ? !PLACEHOLDER_LESSON_SLUGS.has(lesson.slug) : true))
    .map((lesson) => fillMissing(lesson, LESSON_CONTENT_PATCHES[lesson.slug]))
    .filter((lesson) => (shippable ? lessonHasBody(lesson) : true))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  if (shippable && lessons.length === 0) return null;

  return {
    ...module,
    lessons,
  };
}

function withLocalModules(path: JourneyPath): JourneyPath {
  const local = LOCAL_SHIPPABLE_JOURNEYS.find((journey) => journey.slug === path.slug);
  const hasRealLesson = (path.modules || []).some((module) =>
    (module.lessons || []).some((lesson) => lessonHasBody(lesson) || LESSON_CONTENT_PATCHES[lesson.slug])
  );

  if (local && !hasRealLesson) {
    return {
      ...path,
      modules: local.modules,
      shortDescription: path.shortDescription || local.shortDescription,
      introduction: path.introduction && asBlocks(path.introduction).length > 0 ? path.introduction : local.introduction,
      level: path.level || local.level,
    };
  }

  return path;
}

export function isJourneyLaunchReady(path: JourneyPath | null | undefined): boolean {
  if (!path || !MVP_CONFIG.courses.enabled) return false;
  const modules = path.modules || [];
  if (modules.length === 0) return false;

  const lessonCount = modules.reduce((sum, module) => sum + (module.lessons?.length || 0), 0);
  if (lessonCount < 2) return false;

  const everyLessonHasBody = modules.every(
    (module) => (module.lessons || []).length > 0 && (module.lessons || []).every((lesson) => lessonHasBody(lesson))
  );
  if (!everyLessonHasBody) return false;

  if (SHIPPABLE.has(path.slug)) return true;

  // A published course can ship without a local scaffold only when nothing was withheld.
  return !modules.some((module) =>
    (module.lessons || []).some((lesson) => PLACEHOLDER_LESSON_SLUGS.has(lesson.slug))
  );
}

export function prepareJourneyForLaunch(
  path: JourneyPath | null,
  requestedSlug?: string,
  options?: { trustSource?: boolean }
): JourneyPath | null {
  const slug = path?.slug || requestedSlug;
  if (!path) {
    if (options?.trustSource) return null;
    return LOCAL_SHIPPABLE_JOURNEYS.find((journey) => journey.slug === slug) || null;
  }

  const shippable = SHIPPABLE.has(path.slug);
  const seeded = options?.trustSource || !shippable ? path : withLocalModules(path);
  const modules = (seeded.modules || [])
    .map((module) => prepareModule(module, shippable))
    .filter((module): module is Module => module !== null)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const prepared: JourneyPath = {
    ...seeded,
    title: (seeded.title || '').trim() || seeded.title,
    level: seeded.level || 'beginner',
    rating: seeded.rating ?? 4.8,
    enrolled: seeded.enrolled ?? 0,
    isPremium: Boolean(seeded.isPremium),
    modules,
  };

  // An explicit launchReady: false is an admin gate. Missing means "decide from content".
  const adminClosed = seeded.launchReady === false;
  prepared.launchReady = !adminClosed && isJourneyLaunchReady(prepared);
  return prepared;
}

export function prepareJourneyList(paths: JourneyPath[]): JourneyPath[] {
  const prepared = (paths || [])
    .map((path) => prepareJourneyForLaunch(path))
    .filter((path): path is JourneyPath => path !== null);

  const seen = new Set(prepared.map((path) => path.slug));
  LOCAL_SHIPPABLE_JOURNEYS.forEach((journey) => {
    if (!seen.has(journey.slug)) {
      const ready = prepareJourneyForLaunch(journey);
      if (ready) prepared.push(ready);
    }
  });

  prepared.sort((a, b) => {
    if (Boolean(a.launchReady) !== Boolean(b.launchReady)) return a.launchReady ? -1 : 1;
    if (Boolean(a.isPremium) !== Boolean(b.isPremium)) return a.isPremium ? 1 : -1;
    return a.title.localeCompare(b.title);
  });

  return prepared;
}

export function getOfflineLearningCatalog(): JourneyPath[] {
  const offlineNewToAmerica: JourneyPath = {
    _id: 'offline-new-to-america',
    title: 'New to America: Your Essential Guide',
    slug: 'new-to-america',
    duration: '4–6 weeks',
    level: 'beginner',
    isPremium: false,
    rating: 4.8,
    enrolled: 0,
    shortDescription: 'Orientation, a U.S. phone number, and home internet — the part of this guide that is ready to finish.',
    modules: [
      {
        _id: 'offline-welcome',
        title: 'Welcome & Orientation',
        description: 'Emergency number, everyday customs, and a short check.',
        order: 1,
        lessons: [
          {
            ...(LESSON_CONTENT_PATCHES['quiz-1'] as Lesson),
            _id: 'offline-quiz-1',
            title: 'Orientation check',
            slug: 'quiz-1',
            order: 1,
            type: 'quiz',
            duration: '10 min',
          },
        ],
      },
      {
        _id: 'offline-arrival',
        title: 'Arrival & Initial Setup',
        description: 'Phone and internet for your first weeks.',
        order: 2,
        lessons: [
          {
            ...(LESSON_CONTENT_PATCHES['setting-up-your-phone'] as Lesson),
            _id: 'offline-phone',
            title: 'Setting Up Your Phone',
            slug: 'setting-up-your-phone',
            order: 1,
            type: 'reading',
            duration: '15 min',
          },
          {
            ...(LESSON_CONTENT_PATCHES.intenet as Lesson),
            _id: 'offline-internet',
            title: 'Internet at Home',
            slug: 'intenet',
            order: 2,
            type: 'reading',
            duration: '12 min',
          },
        ],
      },
    ],
  };

  return prepareJourneyList([
    offlineNewToAmerica,
    ...LOCAL_SHIPPABLE_JOURNEYS,
    ...OFFLINE_INCOMPLETE_JOURNEYS,
  ]);
}

export function getLaunchInventory() {
  return LAUNCH_INVENTORY;
}

/** Old quick-course ids that should open a real lesson instead of a dead page. */
export const QUICK_COURSE_ALIASES: Record<string, string> = {
  '1': 'banking-system',
  '2': 'credit-basics',
  'banking-basics': 'banking-system',
  'credit-score-101': 'credit-basics',
  'credit-score-basics': 'credit-basics',
  '5-minute-banking-setup': 'banking-system',
  'quick-banking-setup': 'banking-system',
  'housing-search': 'housing-tips',
};

export function resolveQuickCourseId(courseId: string | undefined): string | undefined {
  if (!courseId) return undefined;
  return QUICK_COURSE_ALIASES[courseId] || courseId;
}

/** Fixture shaped like the published Sanity catalog. Used to prove gating without the network. */
export function assertLaunchRules(): string[] {
  const problems: string[] = [];
  const prepared = prepareJourneyList([
    {
      _id: 'nta',
      title: ' New to America: Your Essential Guide',
      slug: 'new-to-america',
      duration: '4–6 weeks',
      level: 'beginner',
      isPremium: false,
      modules: [
        {
          _id: 'welcome',
          title: 'Welcome & Orientation',
          description: 'Start here',
          order: 0,
          lessons: [
            {
              _id: 'welcome-real',
              title: 'Welcome',
              slug: 'welcome-message-and-program-overview',
              order: 1,
              type: 'reading',
              content: [{ _type: 'block', children: [{ _type: 'span', text: 'Welcome.' }] }],
            },
            {
              _id: 'quiz',
              title: 'Quiz 1 ',
              slug: 'quiz-1',
              order: 3,
              type: 'quiz',
            },
          ],
        },
        {
          _id: 'arrival',
          title: 'Arrival & Initial Setup',
          description: 'Phone and internet',
          order: 1,
          lessons: [
            { _id: 'phone', title: 'Setting Up Your Phone', slug: 'setting-up-your-phone', order: 1, type: 'reading' },
            { _id: 'net', title: 'Intenet', slug: 'intenet', order: 2, type: 'reading' },
          ],
        },
        {
          _id: 'docs',
          title: 'Essential Documentation & Legalities',
          description: 'Mixed',
          order: 2,
          lessons: [
            {
              _id: 'credit',
              title: 'Building Credit from Scratch',
              slug: 'building-credit-from-scratch',
              order: 1,
              type: 'reading',
              content: [{ _type: 'block', children: [{ _type: 'span', text: 'Five factors.' }] }],
            },
            {
              _id: 'dup',
              title: 'Lesson 0.1: Introduction 1',
              slug: 'lesson-0-1-introduction-1',
              order: 1,
              type: 'reading',
              content: [{ _type: 'block', children: [{ _type: 'span', text: 'Duplicate culture intro.' }] }],
            },
          ],
        },
        {
          _id: 'empty-health',
          title: 'Healthcare & Wellness',
          description: 'Empty',
          order: 3,
          lessons: [],
        },
      ],
    },
    {
      _id: 'budget',
      title: 'Budgeting for a Fresh Start',
      slug: 'budgeting-start',
      duration: '2–3 weeks',
      level: 'beginner',
      isPremium: false,
      modules: [],
    },
    {
      _id: 'health',
      title: 'Health Insurance Basics',
      slug: 'health-insurance',
      duration: '3–4 weeks',
      level: 'beginner',
      isPremium: false,
      modules: [],
    },
    {
      _id: 'taxes',
      title: 'Understanding U.S. Taxes',
      slug: 'us-taxes',
      duration: '4–5 weeks',
      level: 'beginner',
      isPremium: true,
      modules: [],
    },
    {
      _id: 'golden',
      title: 'Preparing for Your Golden Years',
      slug: 'preparing-for-your-golden-years',
      duration: '3 hours',
      level: 'intermediate',
      isPremium: true,
      modules: [
        {
          _id: 'retire',
          title: 'Introduction to Retirement Accounts',
          description: 'Partial',
          order: 0,
          lessons: [
            { _id: 'ira', title: 'What is an IRA?', slug: 'what-is-an-ira', order: 1, type: 'video', introduction: [{ _type: 'block', children: [{ _type: 'span', text: 'An IRA is powerful.' }] }] },
            {
              _id: '401k',
              title: 'Understanding Your 401(k)',
              slug: 'understanding-your-401-k',
              order: 2,
              type: 'reading',
              content: [{ _type: 'block', children: [{ _type: 'span', text: 'A 401(k) is an employer plan.' }] }],
            },
          ],
        },
      ],
    },
  ]);

  const bySlug = Object.fromEntries(prepared.map((path) => [path.slug, path]));
  const ready = prepared.filter((path) => path.launchReady).map((path) => path.slug);
  if (ready.length < 3) problems.push(`expected at least 3 ready paths, got ${ready.join(', ')}`);

  for (const slug of ['new-to-america', 'budgeting-start', 'health-insurance']) {
    const path = bySlug[slug];
    if (!path?.launchReady) problems.push(`${slug} should be launch ready`);
    const lessonCount = path?.modules.reduce((sum, module) => sum + module.lessons.length, 0) || 0;
    if (lessonCount < 2) problems.push(`${slug} should have at least 2 lessons, got ${lessonCount}`);
    if (path?.modules.some((module) => module.lessons.length === 0)) problems.push(`${slug} still has an empty module`);
    if (path?.modules.some((module) => module.lessons.some((lesson) => !lessonHasBody(lesson)))) {
      problems.push(`${slug} has a lesson without body content`);
    }
  }

  const america = bySlug['new-to-america'];
  const americaSlugs = america?.modules.flatMap((module) => module.lessons.map((lesson) => lesson.slug)) || [];
  if (!americaSlugs.includes('quiz-1')) problems.push('quiz-1 was dropped');
  if (!americaSlugs.includes('setting-up-your-phone')) problems.push('phone lesson was dropped');
  if (!americaSlugs.includes('intenet')) problems.push('internet lesson was dropped');
  if (americaSlugs.includes('lesson-0-1-introduction-1')) problems.push('placeholder intro was not hidden');
  if (america?.modules.some((module) => module.title.includes('Healthcare'))) problems.push('empty healthcare module still visible');
  const internet = america?.modules.flatMap((module) => module.lessons).find((lesson) => lesson.slug === 'intenet');
  if (internet?.title === 'Intenet') problems.push('internet title was not corrected');
  const quiz = america?.modules.flatMap((module) => module.lessons).find((lesson) => lesson.slug === 'quiz-1');
  if (!quiz?.associatedQuiz?.questions?.length) problems.push('quiz-1 has no questions');

  if (bySlug['us-taxes']?.launchReady) problems.push('us-taxes should stay gated');
  if (bySlug['preparing-for-your-golden-years']?.launchReady) problems.push('retirement course should stay gated');
  if ((bySlug['budgeting-start']?.modules.length || 0) < 2) problems.push('budgeting scaffold missing modules');
  if ((bySlug['health-insurance']?.modules.length || 0) < 2) problems.push('health scaffold missing modules');

  const offline = getOfflineLearningCatalog();
  if (!offline.some((path) => path.slug === 'new-to-america' && path.launchReady)) {
    problems.push('offline catalog lost the new-to-america path');
  }
  if (!offline.some((path) => path.slug === 'us-taxes' && path.launchReady !== true)) {
    problems.push('offline catalog should still list taxes as not ready');
  }

  return problems;
}
